/**
 * Patch guard + coalescer for builder reliability.
 * Normalizes paths, coalesces multiple edits per file (last-one-wins),
 * stable-sorts, and enforces MAX_PATCH_ITEMS by failing explicitly (no silent truncation).
 *
 * Before guard validation, normalizePatchSetExtensions rewrites .js/.jsx under src/ to .ts/.tsx
 * and updates import references in content, so PATCH_GUARD_INVALID_EXTENSION is avoided.
 */

export const MAX_PATCH_ITEMS = 25;
export const MAX_PATCH_ITEMS_INITIAL = 100; // Higher limit for initial builds (new apps)

export type PatchEdit = {
  filePath: string;
  action: "create" | "modify" | "delete";
  content?: string;
};

export type PatchInput = {
  filePath?: string;
  path?: string;
  action?: string;
  content?: string;
};

function normalizePatchPath(p: string): string {
  return String(p ?? "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\.\//, "")
    .replace(/^\/+/, "");
}

/** Protected Vite entry files — never shown in builder UI or editable by the pipeline. */
const PROTECTED_ENTRY_PATHS = new Set(["index.html", "src/main.tsx"]);

/** True if path (after normalization) is a protected entry file. */
export function isProtectedEntryPath(path: string): boolean {
  return PROTECTED_ENTRY_PATHS.has(normalizePatchPath(path));
}

/** Filter out index.html and src/main.tsx from path lists (for display only). */
export function filterProtectedPaths(paths: string[]): string[] {
  return paths.filter((p) => !isProtectedEntryPath(p));
}

/**
 * Rewrite patch paths that differ only by case from targetPaths.
 * Builds a lookup: key = lowercase(normalized targetPath), value = exact targetPath.
 * For each edit path, if its lowercase normalized form matches a target, rewrite to the exact targetPath.
 * Only allows case-only matches to existing targetPaths; never introduces new paths.
 */
export function rewritePatchPathsToMatchTargets(
  edits: PatchEdit[],
  targetPaths: string[]
): PatchEdit[] {
  const lowerToExact = new Map<string, string>();
  for (const t of targetPaths) {
    const norm = normalizePatchPath(t);
    if (norm) lowerToExact.set(norm.toLowerCase(), norm);
  }
  if (lowerToExact.size === 0) return edits;

  return edits.map((e) => {
    const norm = normalizePatchPath(e.filePath);
    if (!norm) return e;
    const canonical = lowerToExact.get(norm.toLowerCase());
    if (!canonical || canonical === norm) return e;
    return { ...e, filePath: canonical };
  });
}

/** Detect if content contains JSX. */
export function detectJsx(content: string): boolean {
  const c = typeof content === "string" ? content : "";
  return (
    /<[A-Za-z]/.test(c) ||
    /return\s*\(\s*</.test(c) ||
    /React\.createElement\s*\(/.test(c)
  );
}

/**
 * True if file should use .tsx: already .tsx/.jsx, content has JSX, or path heuristic.
 */
export function shouldBeTsx(path: string, content?: string): boolean {
  const n = normalizePatchPath(path);
  if (n.endsWith(".tsx") || n.endsWith(".jsx")) return true;
  const c = typeof content === "string" ? content : "";
  if (c && detectJsx(c)) return true;
  if (/^src\/(components|pages|screens)\//.test(n)) {
    if (c && /react/i.test(c) && /</.test(c)) return true;
    if (!c) return true;
  }
  return false;
}

/**
 * Normalize extension: any src/** with JSX must be .tsx.
 * Handles .ts -> .tsx upgrade when content has JSX (fixes model proposing .ts with JSX).
 */
export function normalizeExtensionForContent(path: string, content?: string): string {
  const n = normalizePatchPath(path);
  if (!n.startsWith("src/")) return n;
  if (n.endsWith(".tsx")) return n;
  if (n.endsWith(".jsx")) return n.slice(0, -4) + ".tsx";
  if (n.endsWith(".ts")) {
    return shouldBeTsx(n, content) ? n.slice(0, -3) + ".tsx" : n;
  }
  if (n.endsWith(".js")) {
    return shouldBeTsx(n, content) ? n.slice(0, -3) + ".tsx" : n.slice(0, -3) + ".ts";
  }
  return n;
}

/** @deprecated Prefer shouldBeTsx. */
export function detectNeedsTsx(path: string, content?: string): boolean {
  return shouldBeTsx(path, content);
}

/**
 * Rewrite src/ paths to correct extension. Uses normalizeExtensionForContent.
 */
export function normalizePathExtension(path: string, content?: string): string {
  return normalizeExtensionForContent(path, content);
}

/** Map old path -> new path for import rewriting (only when path actually changes). */
type PathRewriteMap = Map<string, string>;

function buildPathRewriteMap(edits: PatchInput[]): PathRewriteMap {
  const map = new Map<string, string>();
  const getAction = (e: PatchInput) => (e?.action ?? "modify") as string;
  for (const e of edits) {
    const raw = e?.filePath ?? e?.path;
    if (raw == null) continue;
    const norm = normalizePatchPath(raw);
    const content =
      (getAction(e) === "create" || getAction(e) === "modify") && typeof e?.content === "string"
        ? e.content
        : undefined;
    const rewritten = normalizePathExtension(norm, content);
    if (rewritten !== norm) {
      map.set(norm, rewritten);
      if (rewritten.endsWith(".tsx")) {
        const base = rewritten.slice(0, -4);
        if (norm !== base + ".ts") map.set(base + ".ts", rewritten);
        if (norm !== base + ".js") map.set(base + ".js", rewritten);
        if (norm !== base + ".jsx") map.set(base + ".jsx", rewritten);
      }
    }
  }
  return map;
}

/**
 * Rewrite import/require specifiers in content that reference old .js/.jsx paths.
 * Replaces e.g. "./assets/catAnimations.js" with "./assets/catAnimations.ts" when that file is in pathMap.
 * Only replaces inside quoted strings to avoid false positives.
 */
function rewriteImportsInContent(content: string, pathMap: PathRewriteMap): string {
  if (!pathMap.size || !content) return content;
  let out = content;
  for (const [oldPath, newPath] of pathMap) {
    const oldFileName = oldPath.split("/").pop() ?? "";
    const newFileName = newPath.split("/").pop() ?? "";
    if (oldFileName === newFileName) continue;
    const escaped = oldFileName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp("([\"'`])([^\"'`]*?)" + escaped + "([\"'`])", "g");
    out = out.replace(re, (_, q1, mid, q2) => q1 + mid + newFileName + q2);
  }
  return out;
}

/**
 * Normalize patch set: rewrite .js/.jsx paths under src/ to .ts/.tsx,
 * and update import references in file contents. Call before guard validation.
 */
export function normalizePatchSetExtensions(edits: PatchInput[]): PatchInput[] {
  const pathMap = buildPathRewriteMap(edits);
  if (pathMap.size === 0) return edits;

  const getAction = (e: PatchInput) => (e?.action ?? "modify") as string;
  return edits.map((e) => {
    const raw = e?.filePath ?? e?.path;
    const normPath = raw != null ? normalizePatchPath(raw) : "";
    const rawContent =
      (getAction(e) === "create" || getAction(e) === "modify") && typeof e?.content === "string"
        ? e.content
        : undefined;
    const path = raw != null ? normalizePathExtension(normPath, rawContent) : undefined;
    const content =
      rawContent != null ? rewriteImportsInContent(rawContent, pathMap) : e?.content;
    const out: PatchInput = { ...e, content };
    if (e?.filePath != null) out.filePath = path ?? raw;
    if (e?.path != null) out.path = path ?? raw;
    return out;
  });
}

function toCanonicalAction(a: string): "create" | "modify" | "delete" {
  if (a === "create" || a === "delete") return a;
  return "modify"; // "update" | "edit" | etc.
}

/** index.html and src/main.tsx must never be edited by the builder (all actions rejected). */
const ALWAYS_PROTECTED = new Set(["index.html", "src/main.tsx"]);
const PROTECTED_FILES = new Set(["src/App.tsx", "vite.config.ts", "package.json"]);

export type PatchGuardErrorCode = "PATCH_GUARD_INVALID_PATH" | "PATCH_GUARD_LIMIT" | "PATCH_GUARD_PROTECTED_FILE" | "PATCH_GUARD_INVALID_EXTENSION" | "PATCH_GUARD_FORBIDDEN_IMPORT";

export type GuardResult = {
  edits: PatchEdit[];
  error?: string;
  errorCode?: PatchGuardErrorCode;
  maxPatchItems?: number;
};

/**
 * Guard and coalesce patch edits: normalize paths, last-one-wins per file,
 * deterministic sort. Fails explicitly if count exceeds max (no truncation).
 * Runs normalizePatchSetExtensions first so .js/.jsx under src/ are rewritten before validation.
 */
/**
 * Case-insensitive dedup: if patch contains both src/App.tsx and src/app.tsx
 * (or any pair differing only by case), keep the canonical-cased version.
 * Canonical = the entry whose normalized path has a capital letter in the
 * filename segment (e.g. App.tsx beats app.tsx). If both are capital or both
 * lowercase, later entry wins.
 */
function deduplicateCaseConflicts(edits: PatchInput[]): PatchInput[] {
  const seen = new Map<string, { index: number; path: string }>();
  const drop = new Set<number>();
  for (let i = 0; i < edits.length; i++) {
    const raw = edits[i]?.filePath ?? edits[i]?.path;
    if (raw == null) continue;
    const norm = normalizePatchPath(raw);
    const lower = norm.toLowerCase();
    const prev = seen.get(lower);
    if (prev && prev.path !== norm) {
      // Case conflict detected — keep the one with capital letter in filename
      const prevFile = prev.path.split("/").pop() ?? "";
      const curFile = norm.split("/").pop() ?? "";
      if (/[A-Z]/.test(curFile) && !/[A-Z]/.test(prevFile)) {
        // Current is canonical (e.g. App.tsx), drop previous (e.g. app.tsx)
        console.warn(`[patchGuard] case-conflict: dropping "${prev.path}" in favor of "${norm}"`);
        drop.add(prev.index);
        seen.set(lower, { index: i, path: norm });
      } else {
        // Previous is canonical or same case — drop current
        console.warn(`[patchGuard] case-conflict: dropping "${norm}" in favor of "${prev.path}"`);
        drop.add(i);
      }
    } else {
      seen.set(lower, { index: i, path: norm });
    }
  }
  if (drop.size === 0) return edits;
  return edits.filter((_, i) => !drop.has(i));
}

export function guardAndCoalescePatches(
  edits: PatchInput[],
  max: number = MAX_PATCH_ITEMS,
  options?: { isInitialBuild?: boolean }
): GuardResult {
  // Use higher limit for initial builds (new apps generate 40-60+ files)
  const effectiveMax = options?.isInitialBuild ? MAX_PATCH_ITEMS_INITIAL : max;
  const normalized = deduplicateCaseConflicts(normalizePatchSetExtensions(edits));
  const byPath = new Map<string, PatchEdit>();
  let droppedInvalid = 0;

  for (const edit of normalized) {
    const raw = edit?.filePath ?? edit?.path;
    if (raw == null) {
      droppedInvalid++;
      continue;
    }
    const path = normalizePatchPath(raw);
    if (!path || path.includes("..")) {
      droppedInvalid++;
      continue;
    }
    if (ALWAYS_PROTECTED.has(path)) {
      return {
        edits: [],
        error: `Protected file cannot be modified: ${path}. Do not edit index.html or src/main.tsx.`,
        errorCode: "PATCH_GUARD_PROTECTED_FILE",
        maxPatchItems: max,
      };
    }
    if (path.startsWith("src/") && (path.endsWith(".js") || path.endsWith(".jsx"))) {
      return {
        edits: [],
        error: `JS/JSX files are not allowed in src/. Use .tsx instead: ${path}`,
        errorCode: "PATCH_GUARD_INVALID_EXTENSION",
        maxPatchItems: max,
      };
    }
    const action = toCanonicalAction(edit?.action ?? "modify");
    const content = edit?.content;
    if ((action === "create" || action === "modify") && content != null && /esm\.sh/i.test(String(content))) {
      return {
        edits: [],
        error: "Forbidden external import: esm.sh is not allowed. Use local deps (react/react-dom locally) and relative .tsx imports only.",
        errorCode: "PATCH_GUARD_FORBIDDEN_IMPORT",
        maxPatchItems: max,
      };
    }
    if (PROTECTED_FILES.has(path) && action === "delete") {
      return {
        edits: [],
        error: `Protected file cannot be deleted: ${path}`,
        errorCode: "PATCH_GUARD_PROTECTED_FILE",
        maxPatchItems: max,
      };
    }
    if (PROTECTED_FILES.has(path) && action === "modify" && (content == null || String(content).trim() === "")) {
      return {
        edits: [],
        error: `Protected file must have content: ${path}`,
        errorCode: "PATCH_GUARD_PROTECTED_FILE",
        maxPatchItems: max,
      };
    }
    byPath.set(path, { filePath: path, action, content });
  }

  const coalesced = [...byPath.values()].sort((a, b) =>
    a.filePath.localeCompare(b.filePath, "en")
  );

  if (coalesced.length > effectiveMax) {
    return {
      edits: [],
      error: `Too many patch items (${coalesced.length}); limit is ${effectiveMax}. Reduce scope or split the request.`,
      errorCode: "PATCH_GUARD_LIMIT",
      maxPatchItems: effectiveMax,
    };
  }

  if (droppedInvalid > 0) {
    return {
      edits: coalesced,
      error: `Some patch paths were invalid and were dropped (${droppedInvalid}).`,
      errorCode: "PATCH_GUARD_INVALID_PATH",
      maxPatchItems: max,
    };
  }

  return { edits: coalesced };
}
